import { useEffect, useState } from 'react';

type Event = {
  id: number;
  ts: string;
  evt: string;
  who: string;
  meta: string;
  dot: 'ok' | 'violet' | 'warn' | 'cyan';
};

const TEMPLATES: Omit<Event, 'id' | 'ts'>[] = [
  { evt: 'auth.sign_in.success', who: 'kira@stellar.dev', meta: 'github', dot: 'ok' },
  { evt: 'auth.mfa.passed', who: 'daichi@aurora.io', meta: 'totp', dot: 'ok' },
  { evt: 'auth.session.refreshed', who: '12 sessions', meta: 'auto', dot: 'violet' },
  { evt: 'auth.sign_in.success', who: 'mira.v@cobalt.run', meta: 'microsoft', dot: 'ok' },
  { evt: 'realtime.channel.joined', who: 'anya@lightyear.app', meta: 'project:a5n', dot: 'cyan' },
  {
    evt: 'auth.rate_limit.triggered',
    who: 'j.okonkwo@iridium.dev',
    meta: '5min cooldown',
    dot: 'warn',
  },
  { evt: 'auth.oauth.linked', who: 'ronan@nimbus.tools', meta: 'okta_sso', dot: 'ok' },
  { evt: 'auth.session.revoked', who: 'sasha@orbital.so', meta: 'manual', dot: 'violet' },
  { evt: 'auth.magic_link.sent', who: 'noah@quartz.ai', meta: 'email', dot: 'ok' },
];

const DOT_CLS: Record<Event['dot'], string> = {
  ok: 'bg-ok',
  violet: 'bg-violet',
  warn: 'bg-warn',
  cyan: 'bg-cyan',
};

function fmtNow() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(
    d.getSeconds()
  ).padStart(2, '0')}`;
}

const MAX = 4;
const APPEND_MS = 2400;

// Stable placeholder for SSR/initial-hydration render — `Date.now()` would
// differ between the build-time server render and the client mount and trigger
// a hydration mismatch. Real timestamps populate in the mount effect.
const PLACEHOLDER_TS = '--:--:--';

/**
 * Live event ticker — drops a new event into the top every ~2.4s, others slide
 * down. Replaces the static "Trusted by" strip with something that actually
 * proves the system is alive. Restart-safe (uses a counter, not random).
 */
export default function LiveEventTicker() {
  const [events, setEvents] = useState<Event[]>(() =>
    TEMPLATES.slice(0, MAX).map((t, i) => ({ ...t, id: i, ts: PLACEHOLDER_TS }))
  );

  useEffect(() => {
    setEvents(prev => prev.map(e => (e.ts === PLACEHOLDER_TS ? { ...e, ts: fmtNow() } : e)));
    let counter = MAX;
    const t = setInterval(() => {
      const tpl = TEMPLATES[counter % TEMPLATES.length];
      const next: Event = { ...tpl, id: counter, ts: fmtNow() };
      counter++;
      setEvents(prev => [next, ...prev].slice(0, MAX));
    }, APPEND_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface shadow-hair">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="relative grid place-items-center">
            <span className="absolute size-2.5 animate-pulse-soft rounded-full bg-ok/30" />
            <span className="size-1.5 rounded-full bg-ok" />
          </span>
          <span className="text-[13px] font-semibold text-ink">Live · auth events</span>
          <span className="font-mono text-[11px] text-muted">streaming from us-east-1</span>
        </div>
        <span className="font-mono text-[11px] text-muted">/v1/audit/stream</span>
      </div>
      <ul className="divide-y divide-slate100">
        {events.map(e => (
          <li
            key={e.id}
            className="grid grid-cols-12 items-center gap-2 px-5 py-2 font-mono text-[12px] animate-ticker-in"
          >
            <span className="col-span-2 text-muted num-tab">{e.ts}</span>
            <span className="col-span-5 flex items-center gap-2 font-semibold text-ink">
              <span className={`size-1.5 rounded-full ${DOT_CLS[e.dot]}`} />
              {e.evt}
            </span>
            <span className="col-span-3 text-body truncate">{e.who}</span>
            <span className="col-span-2 text-right text-muted">{e.meta}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
